//@flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'common/components/core/router';
import { compose } from 'recompose';
import PjpMetricCard from './PjpMetricCard';
import defaultTheme from './PjpMetricPreview.scss';
import { getClientDimensionsMapping } from 'common/redux/selectors/clientSelector';
import UnitConversionHelper from 'common/helpers/UnitConversionHelper';
import { Columns, Box, Column } from '@locus-taxy/neo-ui';
import { type TFunction } from 'react-i18next';
import { themr, type ThemeType } from 'react-css-themr';

import {
    getConfiguredPjpMetricCard,
    configuredMetricHasData
} from 'common/redux/selectors/pjpInsightsSelectors';
import { type PjpMetricCardProps } from 'common/entity/pjpMaster/pjpDataTypes/insights/PjpMetricCardDataTypes';
import { PjpMetricGraph } from 'common/models/internal/PjpMetricGraph';

import Loader from 'common/components/core/loader/Loader';

type Props = {
    clientId: string,
    theme: ThemeType,
    graph: PjpMetricGraph,
    onClose: () => void,
    pjpMetricCard: {
        currentPlanCard: PjpMetricCardProps,
        existingPlanCard?: PjpMetricCardProps
    },
    isEdit: boolean,
    isMetricLoading: boolean,
    chartHasData: boolean,
    t: TFunction
};

class PjpMetricPreview extends Component<Props> {
    shouldComponentUpdate(nextProps) {
        const { isMetricLoading, pjpMetricCard } = nextProps;
        return (
            this.props.isMetricLoading !== isMetricLoading ||
            this.props.pjpMetricCard !== pjpMetricCard
        );
    }

    render() {
        const {
            graph,
            theme,
            clientId,
            pjpMetricCard,
            isMetricLoading,
            chartHasData,
            t
        } = this.props;
        if (isMetricLoading && !chartHasData) {
            return (
                <Box>
                    <Loader theme={theme} active />
                </Box>
            );
        }
        return (
            <Box>
                <Columns space="small">
                    {pjpMetricCard?.currentPlanCard ? (
                        <Column
                            width={
                                pjpMetricCard?.existingPlanCard
                                    ? '1/2'
                                    : 'fluid'
                            }
                        >
                            <PjpMetricCard
                                customHeight={20}
                                cardType={'Current Beat'}
                                t={t}
                                cardHeight={'400'}
                                minCardHeight={'400px'}
                                currentPlanData={
                                    pjpMetricCard.currentPlanCard.chart.data
                                        .currentPlanData
                                }
                                metricCard={pjpMetricCard.currentPlanCard}
                                disableFooter
                                className={theme.analyticsCard}
                                theme={theme}
                                graph={graph}
                                clientId={clientId}
                                alignLegend={'right'}
                                dialogActive
                                disableExport
                            />
                        </Column>
                    ) : null}
                    {pjpMetricCard?.existingPlanCard ? (
                        <Column width={'1/2'}>
                            <PjpMetricCard
                                customHeight={20}
                                cardType={'Existing Beat'}
                                t={t}
                                existingPlanData={
                                    pjpMetricCard.existingPlanCard.chart.data
                                        .existingPlanData
                                }
                                cardHeight={'400'}
                                minCardHeight={'400px'}
                                metricCard={pjpMetricCard.existingPlanCard}
                                disableFooter
                                className={theme.analyticsCard}
                                theme={theme}
                                graph={graph}
                                clientId={clientId}
                                alignLegend={'right'}
                                dialogActive
                                disableExport
                            />
                        </Column>
                    ) : null}
                </Columns>
            </Box>
        );
    }
}

const mapStateToProps = (state, ownProps: Props) => {
    const chartHasData = configuredMetricHasData(state, ownProps);
    let pjpMetricCard;
    let dimensionsMapping;
    if (chartHasData) {
        pjpMetricCard = getConfiguredPjpMetricCard(state, ownProps);
        dimensionsMapping = getClientDimensionsMapping(state, ownProps);
    }
    const unitConverter = new UnitConversionHelper({ dimensionsMapping });
    return {
        ...ownProps,
        pjpMetricCard,
        unitConverter,
        chartHasData
    };
};

const ThemedPjpMetricPreviewElement = compose(
    themr('MetricPreviewElement', defaultTheme),
    withRouter,
    connect(mapStateToProps)
)(PjpMetricPreview);

export { ThemedPjpMetricPreviewElement as PjpMetricPreview };
export default ThemedPjpMetricPreviewElement;
